import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../api/tables";
import { format } from "date-fns";
import { Reservation } from "../../api/reservations";
import { convertPhoneNumberToLink, getReadablePaymentStatus } from "../../util/general-utility";

import Typography from "@material-ui/core/Typography";
import Icon from "@material-ui/core/Icon";
import { ConnectedInfobar } from "../layout/infobar";
import { toggleInfoBar } from "../../actions/store/overview-actions";
import { Meal } from "../../api/meals";

export interface ReservationInfobarProps {}

interface ConnectedReservationInfobarProps extends WithStyles<typeof styles> {
	activeReservationId: string;
	reservations: Reservation[];
	tables: Table[];
	infoBarVisible: boolean;
	availableMeals: Meal[];
	toggleInfoBar: () => void;
}

type Props = ReservationInfobarProps &
	ConnectedReservationInfobarProps &
	InjectedTranslateProps;

export class ReservationInfobar extends React.Component<Props, {}> {
	handleCloseClick = () => {
		this.props.toggleInfoBar();
	};

	render() {
		const {
			classes,
			reservations,
			activeReservationId,
			infoBarVisible,
			availableMeals
		} = this.props;

		const reservation =
			activeReservationId !== null
				? reservations.filter(r => r.id === activeReservationId)[0]
				: null;

		return (
			<ConnectedInfobar
				visible={infoBarVisible}
				renderSaveButton={false}
				onCancelClick={this.handleCloseClick}
				zIndex={5}
				contentClass={classes.root}
			>
				<div className={classes.listIconContainer}>
					<Icon className={classes.listIcon}>info_outlined</Icon>
				</div>
				{reservation ? (
					<div className={classes.content}>
						<div className={classes.list}>
							{reservation.peopleCount && (
								<div className={classes.cell}>
									<Typography className={classes.label}>
										Personenanzahl
									</Typography>
									<Typography className={classes.value}>
										{reservation.peopleCount}{" "}
										{reservation.peopleCount > 1
											? "Personen"
											: "Person"}
									</Typography>
								</div>
							)}
							{reservation.primaryName && (
								<div className={classes.cell}>
									<Typography className={classes.label}>
										Primärname
									</Typography>
									<Typography className={classes.value}>
										{reservation.primaryName}
									</Typography>
								</div>
							)}
							{reservation.time && (
								<div className={classes.cell}>
									<Typography className={classes.label}>
										Uhrzeit
									</Typography>
									<Typography className={classes.value}>
										{format(reservation.time, "HH:mm")}
									</Typography>
								</div>
							)}
							{reservation.phoneNumber && (
								<div className={classes.cell}>
									<Typography className={classes.label}>
										Telefonnummer
									</Typography>
									<Typography className={classes.value}>
										<a
											href={convertPhoneNumberToLink(
												reservation.phoneNumber
											)}
											title={reservation.phoneNumber}
										>
											{reservation.phoneNumber}
										</a>
									</Typography>
								</div>
							)}
							{reservation.email && (
								<div className={classes.cell}>
									<Typography className={classes.label}>
										E-Mail Adresse
									</Typography>
									<Typography className={classes.value}>
										<a
											href={"mailto:" + reservation.email}
											title={reservation.email}
										>
											{reservation.email}
										</a>
									</Typography>
								</div>
							)}
							{reservation.payment && (
								<div className={classes.cell}>
									<Typography className={classes.label}>
										Zahlung
									</Typography>
									<Typography className={classes.value}>
										<span className={classNames(classes.paymentState, {
											[classes.paymentNotCompleted]: reservation.payment.state !== "completed",
											[classes.paymentCharged]: reservation.payment.chargeState === "paid",
										})}>
											{getReadablePaymentStatus(reservation.payment, true)}
										</span>
									</Typography>
								</div>
							)}
						</div>
						{(reservation.notes || (reservation.meals && Object.keys(reservation.meals).length > 0)) && (
							<div className={classes.notesContainer}>
								<Typography className={classes.notesValue}>
									{(reservation.notes) && (
										<span>{reservation.notes}</span>
									)}
									{(reservation.meals && Object.keys(reservation.meals).length > 0) && (
										<span>
											{" | "}Speisen:{" "}
											{Object.keys(reservation.meals).map((id, i, arr) => (
												<span>
													<span key={id}>{reservation.meals[id]}x {availableMeals.find(m => m.id === id).name},{" "}</span>
												</span>
											))}
										</span>
									)}
								</Typography>
							</div>
						)}
					</div>
				) : (
					<div className={classes.noReservationLabelContainer}>
						<Typography className={classes.noReservationLabel}>
							Keine Reservierung ausgewählt!
						</Typography>
					</div>
				)}
			</ConnectedInfobar>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	activeReservationId: state.overviewActiveReservationId,
	reservations: state.reservations,
	tables: state.tables,
	infoBarVisible: state.overviewInfoBarVisible,
	availableMeals: state.settings.meals
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	toggleInfoBar: () => dispatch(toggleInfoBar())
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: "flex",
			alignItems: "center"
		},
		listIconContainer: {
			flexGrow: 0,
			fontSize: 0,
			paddingRight: 25
		},
		listIcon: {
			fontSize: "3rem",
			color: "transparent",
			display: "block",
			backgroundColor: "#a7a9ab",
			textShadow: "1px 1px 1px rgba(255, 255, 255, 0.5)",
			"-webkit-background-clip": "text",
			backgroundClip: "text"
		},
		content: {
			flexGrow: 1
		},
		list: {
			display: "flex",
			flexWrap: "wrap"
		},
		cell: {
			width: "33.33%",
			display: "flex"
		},
		label: {
			flexGrow: 0,
			width: 130,
			fontWeight: 500
		},
		value: {
			flexGrow: 1,

			"& a": {
				textDecoration: "none",
				color: theme.palette.text.primary
			}
		},
		notesContainer: {
			borderTop: "1px solid lightgray",
			marginTop: 5,
			paddingTop: 5
		},
		notesValue: {
			fontStyle: "italic"
		},
		noReservationLabelContainer: {},
		noReservationLabel: {
			fontSize: "1.5rem",
			fontWeight: 500,
			color: "transparent",
			display: "block",
			backgroundColor: "#a7a9ab",
			textShadow: "1px 1px 1px rgba(255, 255, 255, 0.5)",
			"-webkit-background-clip": "text",
			backgroundClip: "text"
		},
		paymentState: { color: "green" },
		paymentNotCompleted: { color: "red" },
		paymentCharged: { color: theme.status.reservation.charged }
	});

export const ConnectedReservationInfobar = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(ReservationInfobar)
	)
);
